// @flow

import React, { useRef, useCallback, type Node, type ElementProps, type Ref, useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { LANGUAGES_BY_COUNTRY, type Language } from '@tectonics/translate/enums/language-by-country.enum';
import { useLanguage } from '@tectonics/controller/language/LanguageContext';

type Props = {
  +country?: ?string,
  +value?: ?string,
  +onChange?: (SyntheticInputEvent<HTMLSelectElement>) => Promise<void>,
};

type SelectEvent = SyntheticInputEvent<HTMLSelectElement>;

function selectLanguagesByCountry(country: string): Language[] {
  return LANGUAGES_BY_COUNTRY[country] || [];
}

function SelectLangInternal({ country, value, onChange, ...props }: Props, ref: Ref<'select'>): Node {
  const ownSelectRef = useRef();
  const selectRef: any = ref || ownSelectRef;

  const [isLoading, setIsLoading] = useState(true);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [language, setLanguage] = useState();
  const { changeLanguage, currentLanguage } = useLanguage();

  useEffect(() => {
    if (value !== undefined && country) {
      const selectedLanguages: Language[] = selectLanguagesByCountry(country);
      setLanguages(selectedLanguages);
      setIsLoading(false);
    }
  }, [value, country]);

  const handleChange = useCallback(async (event: SelectEvent) => {
    const lang = event.currentTarget.value;
    await changeLanguage(lang);
    setLanguage(lang);
    if (onChange) {
      onChange(event);
    }
  }, [onChange]);

  const isMultiLanguage: boolean = useMemo((): boolean => {
    return languages.length > 1;
  }, [languages]);

  return (
    <div>
      {!isLoading && isMultiLanguage && (
        <LangContainer>
          <header>
            <label htmlFor="select-lang">Choose a language</label>
          </header>
          <aside>
            <select {...props} aria-label="Choose a language" ref={selectRef} value={currentLanguage || language} onChange={handleChange}>
              {languages.map((lang, key: number) => (
                <option key={key} value={lang.code}>
                  {lang.language}
                </option>
              ))}
            </select>
          </aside>
        </LangContainer>
      )}
    </div>
  );
}

const LangContainer = styled.div`
  position: fixed;
  width: 282px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-top-left-radius: 7px;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  background-color: white;
  box-shadow: 0 1px 13px 0 rgba(76, 76, 76, 0.15);
  padding: 5px;
  header {
    margin-bottom: 5px;
  }
  label {
    color: #003199;
  }
  aside {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-around;
  }
  select {
    border: 1px solid #c1c1c1;
    background-color: #fff;
    border-radius: 5px;
  }
`;

export const SelectLang = React.forwardRef<ElementProps<typeof SelectLangInternal>, HTMLSelectElement>(SelectLangInternal);
